import React, { useState } from 'react'
import { cn } from '@/lib/utils/cn'
import { initialsFromName } from '@/lib/utils/strings'

/* ------------------------------------------------------------------ */
/*  Avatar — image with initials fallback                              */
/*  Sizes: xs | sm | md | lg | xl                                      */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Image source URL */
  src?: string | null
  /** Alt text for the image */
  alt?: string
  /** Full name — used for initials fallback */
  name?: string
  /** Size variant */
  size?: AvatarSize
  /** Show ring around avatar */
  ring?: boolean
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
}

/**
 * Deterministic color based on name — gives each user a consistent pastel bg.
 */
function nameToColor(name: string): string {
  const colors = [
    'bg-primary-100 text-primary-700',
    'bg-accent-100 text-accent-600',
    'bg-success-bg text-success-500',
    'bg-warning-bg text-warning-500',
    'bg-info-bg text-info-500',
    'bg-neutral-200 text-neutral-700',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length] ?? colors[0]!
}

export function Avatar({
  src,
  alt = '',
  name = '',
  size = 'md',
  ring = false,
  className,
  ...props
}: AvatarProps) {
  const [imgError, setImgError] = useState(false)
  const showImage = src && !imgError

  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        'select-none font-medium',
        sizeClasses[size],
        !showImage && nameToColor(name),
        ring && 'ring-2 ring-primary-200 ring-offset-2',
        className,
      )}
      {...props}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt || name}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span aria-hidden="true">{initialsFromName(name) || '?'}</span>
      )}
    </span>
  )
}

Avatar.displayName = 'Avatar'
